/**
 * Homepage free-training slide-in.
 *
 * Slides in from the bottom-right once a visitor scrolls past the hero and
 * offers Canira's own automated training at its next real start time. The
 * admin decides everything it says (Admin Hub → Homepage training card,
 * `/admin/home-training`); `GET /api/site-config/home-training` returns
 * `{ enabled: false }` for every reason it shouldn't show, and then nothing
 * renders at all.
 *
 * Rules worth knowing before changing it:
 *  - The countdown is to a slot the schedule really offers, and the sign-up
 *    posts that same slot as `selectedStartTime`. Never a fake timer that
 *    resets on reload — that is the version of this pattern that costs trust.
 *  - Signed-in users don't see it (they're customers). `?training=preview`
 *    shows it anyway, so an admin can check it while signed in.
 *  - It waits for the cookie banner to be answered: both sit bottom-right and
 *    a visitor shouldn't have to close two things at once.
 *  - Closing it hides it for a few days; registering hides it for a month.
 *    Both are per-browser conveniences, so a storage error just means it
 *    may show again.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getCookieConsent } from '../CookieConsent';
import {
  nextSlot,
  countdownClock,
  cardBlurb,
  slotLabel,
  shouldReveal,
  isHidden,
  snooze,
  markRegistered,
  registrationErrorCopy,
  isValidEmail,
} from '../../utils/homeTraining';
import './HomeTrainingCard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function safeLocalStorage() {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function consentAnswered() {
  try {
    return !!getCookieConsent();
  } catch {
    return true;
  }
}

const HomeTrainingCard = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const preview = useMemo(
    () => new URLSearchParams(location.search).get('training') === 'preview',
    [location.search],
  );

  const [training, setTraining] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [consentDone, setConsentDone] = useState(consentAnswered);
  const [hidden, setHidden] = useState(() => !preview && isHidden(safeLocalStorage()));
  const [now, setNow] = useState(() => Date.now());
  const [form, setForm] = useState({ firstName: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  // The picture that failed to load, so it's dropped rather than shown as a
  // broken image. Keyed on the URL: a refetch that brings a new link tries again.
  const [failedImage, setFailedImage] = useState(null);
  const refetchingRef = useRef(false);

  const wanted = preview || (!currentUser && !hidden);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/site-config/home-training`);
      const data = r.ok ? await r.json() : null;
      setTraining(data?.enabled ? data : null);
    } catch {
      setTraining(null);
    }
  }, []);

  useEffect(() => {
    if (wanted) load();
  }, [wanted, load]);

  useEffect(() => {
    if (!wanted || scrolled) return undefined;
    const onScroll = () => {
      if (shouldReveal(window.scrollY, window.innerHeight)) setScrolled(true);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [wanted, scrolled]);

  useEffect(() => {
    if (consentDone) return undefined;
    const onChange = () => setConsentDone(true);
    window.addEventListener('cookieConsentChanged', onChange);
    return () => window.removeEventListener('cookieConsentChanged', onChange);
  }, [consentDone]);

  const visible = wanted && !!training && scrolled && (consentDone || preview);

  // Tick only while the card is on screen and still counting down.
  useEffect(() => {
    if (!visible || done) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [visible, done]);

  const slot = training ? nextSlot(training.times, now) : null;

  // Every offered slot has passed while the page sat open: ask for new ones.
  useEffect(() => {
    if (!visible || slot || done || refetchingRef.current) return;
    refetchingRef.current = true;
    load().finally(() => { refetchingRef.current = false; });
  }, [visible, slot, done, load]);

  const close = () => {
    if (!preview) snooze(safeLocalStorage());
    setHidden(true);
    setTraining(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !slot) return;
    const firstName = form.firstName.trim();
    const email = form.email.trim();
    if (!firstName) { setError('Add your first name.'); return; }
    if (!isValidEmail(email)) { setError('Add a valid email address.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/webinars/${training.webinarId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, email, selectedStartTime: slot }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        const copy = registrationErrorCopy(r.status, body);
        if (/already registered/i.test(body?.message || '')) {
          markRegistered(safeLocalStorage());
          setDone({ email, joinUrl: null, slot, already: true });
        } else {
          setError(copy);
        }
        return;
      }
      if (body.requiresPayment) {
        // The server only enables inline sign-up for a free webinar; if that
        // changed underneath us, don't pretend a seat was saved.
        setError("This training now needs checkout. Use its registration page instead.");
        return;
      }
      markRegistered(safeLocalStorage());
      // A repeat registration (same or new time) comes back without the link;
      // it was emailed. Show the time the server actually has them down for.
      setDone({
        email,
        joinUrl: body.joinUrl || null,
        slot: body.session?.scheduledStartTime || slot,
        already: !!body.alreadyRegistered,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[HomeTraining] registration failed', err);
      setError("We couldn't reach Canira. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  const remaining = slot ? Date.parse(slot) - now : 0;
  const blurb = cardBlurb(training.blurb, training.title);
  const imageUrl = training.imageUrl && training.imageUrl !== failedImage ? training.imageUrl : null;

  return (
    <aside className={`htc${imageUrl ? ' htc--image' : ''}`} aria-label="Free live training">
      <button type="button" className="htc-close" onClick={close} aria-label="Close free training offer">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* A banner across the top on a desktop, a small square beside the
          title on a phone (the sheet already covers a lot of the screen).
          Decorative: the title beside it says what it is. */}
      <div className="htc-head">
        {imageUrl && (
          <img
            className="htc-image"
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setFailedImage(imageUrl)}
          />
        )}
        <div className="htc-head-text">
          <span className="htc-eyebrow">Free training</span>
          <h2 className="htc-title">{training.title}</h2>
        </div>
      </div>
      {blurb && !done && <p className="htc-blurb">{blurb}</p>}
      {training.bonus && !done && (
        <p className="htc-bonus">
          <span className="htc-bonus-tag">Attendee bonus</span>
          {training.bonus}
        </p>
      )}

      {done ? (
        <div className="htc-done" role="status">
          <p className="htc-done-head">{done.already ? "You're already in." : "You're in!"}</p>
          <p>
            {slotLabel(done.slot, new Date())}. Your watch link is on its way to <strong>{done.email}</strong>.
          </p>
          {done.joinUrl && (
            <a className="htc-btn" href={done.joinUrl}>Open my watch page</a>
          )}
        </div>
      ) : slot ? (
        <>
          {/* One line: when it is, and how long until then, beside the button
              it hurries. */}
          <div className="htc-when">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="htc-when-label">{slotLabel(slot, new Date(now))}</span>
            <span className="htc-countdown" role="timer" aria-label="Time until the training starts">
              <small>Starts in</small> {countdownClock(remaining)}
            </span>
          </div>

          {training.buttonUrl ? (
            <a className="htc-btn" href={training.buttonUrl}>
              Save my free seat <span aria-hidden="true">→</span>
            </a>
          ) : (
            <form className="htc-form" onSubmit={submit} noValidate>
              {/* Side by side: one row instead of two keeps the card short. */}
              <div className="htc-fields">
                <label className="htc-sr" htmlFor="htc-first">First name</label>
                <input
                  id="htc-first"
                  type="text"
                  autoComplete="given-name"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                />
                <label className="htc-sr" htmlFor="htc-email">Email</label>
                <input
                  id="htc-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              {error && <p className="htc-error" role="alert">{error}</p>}
              <button type="submit" className="htc-btn" disabled={submitting}>
                {submitting ? 'Saving your seat…' : <>Save my free seat <span aria-hidden="true">→</span></>}
              </button>
            </form>
          )}
          <p className="htc-fine">Free · No credit card · Watch link by email</p>
        </>
      ) : (
        <p className="htc-when">Finding the next session…</p>
      )}
    </aside>
  );
};

export default HomeTrainingCard;
